//C:\VUE3\platform\src\composables\useModelFileLoader.js
import { ref } from "vue";
import { useFileStore } from "@/store/useFileStore";
import { modelService } from "@/services/modelService";
import { wait } from "@/utils/wait";
import { logDebug, logWarn, logError } from "@/utils/logHelper";
import { CONVERSION_STATUS as S } from "@/constants/statusConstants";

const DEFAULT_INTERVAL = 1_000;
const MAX_INTERVAL     = 5_000;
const MAX_ATTEMPTS     = 60;
const BACKOFF_RATIO    = 1.2;
const POLLING_STATES   = Object.freeze([S.QUEUED, S.PENDING, S.PROCESSING]);

export default function useModelFileLoader() {
  const loading = ref(false);
  const error   = ref(null);
  const store   = useFileStore();

  /* ────────── 내부 헬퍼 ────────── */
  async function poll(id, {
    interval     = DEFAULT_INTERVAL,
    maxAttempts  = MAX_ATTEMPTS,
    backoffRatio = BACKOFF_RATIO,
     signal,
  } = {}) {
    let attempts = 0;
    let res      = { status: S.QUEUED };

  while (POLLING_STATES.includes(res.status)) {
    if (signal?.aborted) { throw new DOMException('Aborted', 'AbortError'); }
    try {
    res = await modelService.getStatus(id, { signal });
      logDebug("poll status:", res);

      // 실패 시 즉시 빠져나가도록 예외 던지기
      if (res.status === S.FAILED) {
        throw new Error(res.message || "Conversion failed");
      }
    } catch (e) {
      // 변환 실패 에러는 통과시켜 루프 탈출
      if (e.message === res.message) {
        throw e;
      }
      // 나머지는 네트워크/서버 에러로 간주
      classifyAndThrow(e);
    }

    if (++attempts > maxAttempts) {
      throw new Error("변환 대기 시간이 초과되었습니다.");
    }
     if (signal?.aborted) { throw new DOMException('Aborted', 'AbortError'); }
    await wait(interval);
    interval = Math.min(interval * backoffRatio, MAX_INTERVAL);
  }

  return res;
  }

  function classifyAndThrow(e) {

   // ── 1) 서버가 변환 실패 응답으로 던진 에러를 즉시 재발사 ──
   if (e.isApiError) {
     // e.message 에는 FastAPI 쪽 message(processing_error 등)가 담겨 있음
     throw new Error(e.message || "Conversion failed");
   }
    // axios 네트워크/타임아웃
    if (e.code === "ECONNABORTED")
      throw new Error("변환 요청 대기 시간이 3분을 초과하여 중단되었습니다.");

    const status = e.response?.status;
    if (status >= 500)
      throw new Error("서버 처리 중 오류가 발생했습니다.");

    const networkErr = e.code === "ERR_NETWORK" || (e.isAxiosError && !e.response);
    if (networkErr)
      throw new Error("서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.");

    // 이 외에는 재시도 가능 로그만 남기고 흘려보냄
    logWarn("getStatus retryable error:", e);
  }

  /* ────────── 퍼블릭 API ────────── */
async function loadModel(file, recordId, opts = {}) {
    if (!recordId) throw new Error("recordId가 필요합니다.");
     const { signal } = opts;

    loading.value = true;
    error.value   = null;
    await store.updateFileAction(recordId, { status: S.CONVERTING });

    try {
      /* ✦ 1) GLB는 로컬에서 바로 처리 */
      const isGlb = file.name.toLowerCase().endsWith(".glb") ||
                    file.type === "model/gltf-binary";

      if (isGlb) {
        const url = URL.createObjectURL(file);
        await store.updateFileAction(recordId, {
          uuid: crypto.randomUUID?.() ?? Date.now().toString(36),
          rawUrl: url,
          glbUrl: url,
          thumbnailUrl: "",
          volume: 0,
          surfaceArea: 0,
          boundingBoxMm: { min: {}, max: {}, tot: {} },
          status: S.COMPLETED,
          success: true,
        });
        logDebug("Local GLB 완료", { recordId });
        return;
      }

      /* ✦ 2) 서버 변환 루트 */
      const form = new FormData();
      form.append("file", file);

      logDebug("Uploading…", file.name);
       const { id } = await modelService.upload(form, { signal });
            const res    = await poll(id, { signal });

      if (res.status !== S.COMPLETED || !res.success)
        throw new Error("Conversion failed");

      await store.updateFileAction(recordId, {
        uuid:          res.uuid,
        rawUrl:        res.raw_url,
        glbUrl:        res.glb_url,
        thumbnailUrl:  res.thumbnail_url,
        volume:        res.volume,
        surfaceArea:   res.surface_area,
        edgeLength:    res.edge_length,
        centerOfMass:  res.center_of_mass,
        boundingBoxMm: res.bounding_box_mm,
        status:        res.status,
        success:       res.success,
      });
      logDebug("Server 변환 완료", { recordId, uuid: res.uuid });

    } catch (err) {
            // 사용자 취소는 조용히 종료 (스토어에 ERROR 반영 금지)
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED' || err?.message === 'USER_CANCELED') {
        return;
      }
      error.value = err;
      logError("loadModel error", err);
      await store.updateFileAction(recordId, {
        status: S.ERROR,
        success: false,
        errorMessage: err.message,
      });
      throw err;

    } finally {
      loading.value = false;
    }
  }

  return { loading, error, loadModel };
}
